import React, { useState, useEffect } from 'react';
import { personalNotes, notes } from '../api';
import './AccessControlPanel.css';

/**
 * AccessControlPanel - Manage which coaches have access to a personal note or all notes
 * Props:
 *   noteId: Single note ID (required if allNotes is false)
 *   allNotes: Boolean to manage access to all notes (default: false)
 *   playerCoaches: Array of coaches (unused, kept for backward compatibility)
 *   onClose: Callback when modal closes
 *   onAccessChanged: Callback when access is changed
 */
const AccessControlPanel = ({ noteId, allNotes = false, playerCoaches = [], onClose, onAccessChanged }) => {
  const [coaches, setCoaches] = useState([]);
  const [accessGrants, setAccessGrants] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [noteId, allNotes]);

  const loadData = async () => {
    // Skip loading if we're in single-note mode but don't have a noteId
    if (!allNotes && !noteId) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Get all coaches for this player
      const coachesResponse = await personalNotes.getCoaches();
      const availableCoaches = coachesResponse.data.coaches || [];

      // Get access status based on mode
      let accessedCoaches = [];

      if (allNotes === true) {
        // Get access status for ALL notes
        const statusResponse = await personalNotes.getAccessStatusForAllNotes();
        accessedCoaches = statusResponse.data?.accessGrants || [];
      } else {
        // Get access status for a specific note
        const statusResponse = await personalNotes.getAccessStatus(noteId);
        accessedCoaches = statusResponse.data?.accessGrants || [];
      }

      // Merge coaches from both sources to handle cases where a coach has access
      // but is not in the player's official roster
      const coachMap = {};

      // Add all available coaches (from students roster)
      availableCoaches.forEach(coach => {
        coachMap[coach._id] = coach;
      });

      // Add any coaches with access that aren't in available coaches list
      // This handles the case where access was granted but coach isn't in roster
      accessedCoaches.forEach(grant => {
        if (!coachMap[grant.coachId]) {
          coachMap[grant.coachId] = {
            _id: grant.coachId,
            name: grant.coachName,
            email: 'unknown@example.com'  // Placeholder for coaches not in roster
          };
        }
      });

      // Convert map back to array
      const mergedCoaches = Object.values(coachMap);

      setCoaches(mergedCoaches);

      // Create a map of coachId -> has access
      const accessMap = {};
      accessedCoaches.forEach(grant => {
        accessMap[grant.coachId] = true;
      });
      setAccessGrants(accessMap);
    } catch (err) {
      console.error('❌ Error loading access data:', err);
      setError('Failed to load access information');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccess = async (coachId, coachName, hasAccess) => {
    setSaving(true);
    setError(null);

    try {
      if (allNotes) {
        // Managing access to ALL notes
        if (hasAccess) {
          // Revoke access from all notes
          await personalNotes.revokeAccessFromAllNotes(coachId);
        } else {
          // Grant access to all notes
          await personalNotes.grantAccessToAllNotes(coachId, coachName);
        }
      } else {
        // Managing access to a single note
        if (hasAccess) {
          // Revoke access from this note
          await personalNotes.revokeAccess(noteId, coachId);
        } else {
          // Grant access to this note
          await personalNotes.grantAccess(noteId, coachId, coachName);
        }
      }

      // Update local state
      setAccessGrants(prev => ({
        ...prev,
        [coachId]: !hasAccess
      }));

      // Refresh parent
      if (onAccessChanged) {
        onAccessChanged();
      }
    } catch (err) {
      console.error('❌ Error updating access:', err);
      setError(err.response?.data?.message || 'Failed to update access');
    } finally {
      setSaving(false);
    }
  };

  const activeCoaches = coaches.filter(coach => accessGrants[coach._id]);
  const inactiveCoaches = coaches.filter(coach => !accessGrants[coach._id]);

  return (
    <div className="modal-overlay">
      <div className="access-control-panel">
        <div className="panel-header">
          <h3>{allNotes ? '📔 Manage Access to All Personal Notes' : '🔓 Manage Coach Access'}</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {error && (
          <div className="panel-error">
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <div className="panel-loading">
            Loading coaches...
          </div>
        ) : coaches.length === 0 ? (
          <div className="panel-empty">
            <p>No coaches in your roster yet</p>
            <p>Add coaches to grant them access to {allNotes ? 'all your personal notes' : 'this note'}</p>
          </div>
        ) : (
          <>
            {/* Active Access Section */}
            {activeCoaches.length > 0 && (
              <div className="access-section active-section">
                <h4>✅ Has Access ({activeCoaches.length})</h4>
                <p className="section-description">
                  These coaches can already view and comment on {allNotes ? 'all your personal notes' : 'this note'}
                </p>
                <div className="coach-list">
                  {activeCoaches.map(coach => (
                    <div key={coach._id} className="coach-item active-coach">
                      <div className="coach-info">
                        <p className="coach-name">✓ {coach.name}</p>
                        <p className="coach-email">{coach.email}</p>
                      </div>
                      <toggle className="access-toggle">
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={() =>
                            handleToggleAccess(coach._id, coach.name, true)
                          }
                          onClick={() =>
                            handleToggleAccess(coach._id, coach.name, true)
                          }
                          disabled={saving}
                          title="Toggle access for this coach"
                        />
                        <span
                          className="toggle-slider"
                          onClick={() =>
                            !saving && handleToggleAccess(coach._id, coach.name, true)
                          }
                        ></span>
                      </toggle>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inactive Access Section */}
            {inactiveCoaches.length > 0 && (
              <div className="access-section inactive-section">
                <h4>🔒 No Access ({inactiveCoaches.length})</h4>
                <p className="section-description">Click the toggle to grant access to a coach</p>
                <div className="coach-list">
                  {inactiveCoaches.map(coach => (
                    <div key={coach._id} className="coach-item inactive-coach">
                      <div className="coach-info">
                        <p className="coach-name">{coach.name}</p>
                        <p className="coach-email">{coach.email}</p>
                      </div>
                      <toggle className="access-toggle">
                        <input
                          type="checkbox"
                          checked={false}
                          onChange={() =>
                            handleToggleAccess(coach._id, coach.name, false)
                          }
                          onClick={() =>
                            handleToggleAccess(coach._id, coach.name, false)
                          }
                          disabled={saving}
                          title="Toggle access for this coach"
                        />
                        <span
                          className="toggle-slider"
                          onClick={() =>
                            !saving && handleToggleAccess(coach._id, coach.name, false)
                          }
                        ></span>
                      </toggle>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Access Info */}
            <div className="access-info">
              <p>
                💡 <strong>Coaches with access can:</strong> View {allNotes ? 'all your personal notes' : 'this note'} and add comments
              </p>
              <p>
                🔒 <strong>They cannot:</strong> Edit or delete {allNotes ? 'your notes' : 'this note'} (read-only)
              </p>
            </div>
          </>
        )}

        <div className="panel-footer">
          <button className="btn-close-panel" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessControlPanel;
